import { IntlShape } from "react-intl";
import { UseFormReturn } from "react-hook-form";

import { isValidSpecialRequirementAdditionalInformation } from "helpers/validationHelpers";

export type ServiceRequirementDataType = "string" | "integer" | "double" | "email";

type ServiceRequirementDef = {
  value: string;
  data_type?: ServiceRequirementDataType;
  additional_information_required?: boolean;
};

function getRequiredMessage(intl: IntlShape) {
  return intl.formatMessage({
    id: "request-form.service.requirements.validation.required",
    defaultMessage: "This is required",
  });
}

function isRequirementValueAnswered(value: unknown): value is "true" | "false" {
  return value === "true" || value === "false";
}

function getValidationMessage(intl: IntlShape, dataType: ServiceRequirementDataType) {
  switch (dataType) {
    case "integer":
      return intl.formatMessage({
        id: "request-form.service.requirements.validation.integer",
        defaultMessage: "Must be a valid integer.",
      });
    case "double":
      return intl.formatMessage({
        id: "request-form.service.requirements.validation.double",
        defaultMessage: "Must be a valid number using digits and a decimal point only (e.g. 3.14 or -3.14).",
      });
    case "email":
      return intl.formatMessage({
        id: "request-form.service.requirements.validation.email",
        defaultMessage: "Must be a valid email address.",
      });
    case "string":
    default:
      return getRequiredMessage(intl);
  }
}

export function getServiceRequirementAdditionalInfoError(
  value: unknown,
  dataType: ServiceRequirementDataType,
  isAdditionalInfoRequired: boolean,
  intl: IntlShape,
): true | string {
  if (isValidSpecialRequirementAdditionalInformation(value, dataType, isAdditionalInfoRequired)) {
    return true;
  }

  const isEmpty = !String(value ?? "").trim();
  return isEmpty ? getRequiredMessage(intl) : getValidationMessage(intl, dataType);
}

export function validateServiceRequirements(
  methods: UseFormReturn,
  intl: IntlShape,
  requestServiceRequirement: boolean | undefined,
  requirementValues: ServiceRequirementDef[] | undefined,
): boolean {
  if (!requestServiceRequirement || !requirementValues?.length) {
    return true;
  }

  let isValid = true;

  requirementValues.forEach((item) => {
    const valueFieldPath = `service.special_requirements.${item.value}.value`;
    const additionalInfoFieldPath = `service.special_requirements.${item.value}.additional_information`;
    const requirementValue = methods.getValues(valueFieldPath);

    if (!isRequirementValueAnswered(requirementValue)) {
      methods.setError(valueFieldPath, { type: "custom", message: getRequiredMessage(intl) });
      methods.clearErrors(additionalInfoFieldPath);
      isValid = false;
      return;
    }

    methods.clearErrors(valueFieldPath);

    if (requirementValue !== "true") {
      methods.clearErrors(additionalInfoFieldPath);
      return;
    }

    const isAdditionalInfoRequired = item.additional_information_required ?? true;
    const additionalInformation = methods.getValues(additionalInfoFieldPath);
    const dataType = item.data_type ?? "string";
    const error = getServiceRequirementAdditionalInfoError(
      additionalInformation,
      dataType,
      isAdditionalInfoRequired,
      intl,
    );

    if (error === true) {
      methods.clearErrors(additionalInfoFieldPath);
      return;
    }

    methods.setError(additionalInfoFieldPath, { type: "custom", message: error });
    isValid = false;
  });

  return isValid;
}
